// src/layouts/trafficmonitoring/routeFlows/groupRouteFlows.js
import i18n from "i18n";
import { routeFlowsData } from "./routeFlowsData";
import { generateShades } from "./functions";

/** Constant colors for the legend boxes */
const FLOW_LABEL_COLOR = "#0095b3";
const TYPICAL_LABEL_COLOR = "#CE2452";

/** Per-series shade settings */
const flowShadeCfg = { hue: 190, sat: 70, lightMin: 30, lightMax: 75 };
const typicalShadeCfg = { hue: 344, sat: 70, lightMin: 40, lightMax: 75 };

/** Group schema by groupId (fallback to id) */
function groupSchema(schema) {
  return Object.values(
    (schema || []).reduce((acc, entry) => {
      const key = entry.groupId ?? entry.id;
      if (!acc[key]) acc[key] = [];
      acc[key].push(entry);
      return acc;
    }, {})
  );
}

/** Align series based on common labels (defensive) */
function alignSeries(actualChart, typicalChart) {
  const labelsA = actualChart?.data?.labels || [];
  const labelsT = typicalChart?.data?.labels || [];

  // fast path: identical and in the same order
  if (labelsA.length === labelsT.length && labelsA.every((l, i) => l === labelsT[i])) {
    const idx = labelsA.map((_, i) => i);
    return { labels: labelsA, idxA: idx, idxT: idx };
  }

  // intersect by label text
  const mapT = new Map(labelsT.map((l, i) => [l, i]));
  const labels = [];
  const idxA = [];
  const idxT = [];

  labelsA.forEach((l, i) => {
    if (mapT.has(l)) {
      labels.push(l);
      idxA.push(i);
      idxT.push(mapT.get(l));
    }
  });

  return { labels, idxA, idxT };
}

/** Custom legend with fixed colors + v3-safe toggle */
function buildLegendConfig() {
  return {
    onClick: (e, legendItem, legend) => {
      const chart = legend.chart;
      const i = legendItem?.datasetIndex;
      if (i == null) return;
      const meta = chart.getDatasetMeta(i);
      if (!meta) return;
      meta.hidden = meta.hidden === null ? !chart.data.datasets[i].hidden : null;
      chart.update();
    },
    labels: {
      generateLabels: function (chart) {
        return chart.data.datasets.map((ds, i) => ({
          text: ds.label,
          fillStyle: i === 0 ? FLOW_LABEL_COLOR : TYPICAL_LABEL_COLOR,
          lineWidth: 1,
          hidden: !chart.isDatasetVisible(i),
          index: i,
        }));
      },
    },
  };
}

/**
 * Combines Actual + Typical per groupId into ONE chart with 2 datasets.
 * @param {Array} schema
 * @param {{date?: string|null, signal?: AbortSignal}} options
 * @returns {Promise<Array<{id: any, data: {labels: string[], datasets: any[]}, options: object}>>}
 */
export async function groupRouteFlows(schema, options = {}) {
  const groups = groupSchema(schema);

  const results = await Promise.all(
    groups.map(async (group) => {
      // fetch for each entry of the group (e.g., Actual & Typical)
      const fetched = await Promise.all(group.map((ep) => routeFlowsData(ep, options)));

      // Keep only successful ones, with schema entry info
      const valid = fetched
        .map((chart, i) => (chart ? { chart, entry: group[i] } : null))
        .filter(Boolean);

      if (valid.length < 1) return null;

      // If only one is available (e.g., Typical failed), return single-series chart
      if (valid.length === 1) {
        const { chart, entry } = valid[0];
        return {
          id: entry.groupId ?? entry.id,
          data: chart.data,
          options: {
            ...chart.options,
            plugins: {
              ...(chart.options?.plugins || {}),
              legend: { display: true, ...buildLegendConfig() },
              title: {
                display: true,
                text: entry.groupName || entry.name,
                font: { size: 16 },
                color: "#fff",
              },
            },
          },
        };
      }

      // Ordering: smaller id = Actual, larger = Typical
      const [first, second] = group[0].id < group[1].id ? [valid[0], valid[1]] : [valid[1], valid[0]];
      const actual = first.chart;
      const typical = second.chart;
      const actualEntry = first.entry;
      const typicalEntry = second.entry;

      // Align labels
      const { labels, idxA, idxT } = alignSeries(actual, typical);

      const aValsRaw = actual?.data?.datasets?.[0]?.data || [];
      const tValsRaw = typical?.data?.datasets?.[0]?.data || [];

      const actualValues = idxA.map((i) => aValsRaw[i] ?? 0);
      const typicalValues = idxT.map((i) => tValsRaw[i] ?? 0);

      // Shades (per bar)
      const actualColors = generateShades(actualValues, flowShadeCfg);
      const typicalColors = generateShades(typicalValues, typicalShadeCfg);

      const datasets = [
        {
          label: i18n.t("trafficMonitoring.routeFlows.actual"),
          data: actualValues,
          backgroundColor: actualColors,
          maxBarThickness: 30,
          borderWidth: 0,
        },
        {
          label: i18n.t("trafficMonitoring.routeFlows.typical"),
          data: typicalValues,
          backgroundColor: typicalColors,
          maxBarThickness: 30,
          borderWidth: 0,
        },
      ];

      return {
        id: actualEntry.groupId ?? actualEntry.id,
        data: { labels, datasets },
        options: {
          ...actual.options,
          plugins: {
            ...(actual.options?.plugins || {}),
            legend: { display: true, ...buildLegendConfig() },
            title: {
              display: true,
              text:
                actualEntry.groupName ||
                actualEntry.name ||
                typicalEntry.groupName ||
                typicalEntry.name,
              font: { size: 16 },
              color: "#fff",
            },
          },
        },
      };
    })
  );

  return results.filter(Boolean);
}
